import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { formatLocalTime } from '../services/timeUtils';

function DiagnosticPage() {
  const [diagnosticInfo, setDiagnosticInfo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Função para formatar data/hora
  const formatDate = (dateString: string) => {
    return formatLocalTime(dateString);
  };

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        setLoading(true);
        const data = await api.getDiagnosticInfo();
        setDiagnosticInfo(data);
        setLoading(false);
      } catch (err) {
        console.error('Erro ao buscar informações de diagnóstico:', err);
        setError('Erro ao buscar informações de diagnóstico. Por favor, tente novamente.');
        setLoading(false);
      }
    };

    fetchDiagnostics();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-4xl bg-white rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Diagnóstico do Sistema</h1>
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-4xl bg-white rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Diagnóstico do Sistema</h1>
          <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-blue-600 p-6">
          <h1 className="text-2xl font-bold text-white">Diagnóstico do Sistema</h1>
        </div>
        <div className="p-6">
          {diagnosticInfo && (
            <div className="space-y-6">
              {/* Informações do Servidor */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Informações do Servidor</h2>
                </div>
                <div className="p-4">
                  {diagnosticInfo.diagnostic_info && diagnosticInfo.diagnostic_info.package && (
                    <div className="mb-4">
                      <h3 className="font-medium mb-2 text-gray-900">Versão do Aplicativo</h3>
                      <p className="text-gray-800">
                        {diagnosticInfo.diagnostic_info.package.name} v{diagnosticInfo.diagnostic_info.package.version}
                      </p>
                    </div>
                  )}

                  {diagnosticInfo.diagnostic_info && diagnosticInfo.diagnostic_info.server_time && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="font-medium mb-2 text-gray-900">Horário do Servidor (GMT-3)</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <p className="text-gray-800">Timestamp: <span className="font-mono text-black">{formatDate(diagnosticInfo.diagnostic_info.server_time.server_timestamp)}</span></p>
                        <p className="text-gray-800">Data: <span className="font-mono text-black">{diagnosticInfo.diagnostic_info.server_time.server_date}</span></p>
                        <p className="text-gray-800">Hora: <span className="font-mono text-black">{diagnosticInfo.diagnostic_info.server_time.current_hour}:{diagnosticInfo.diagnostic_info.server_time.current_minute.toString().padStart(2, '0')}</span></p>
                        <p className="text-gray-800">Formatado: <span className="font-mono text-black">{diagnosticInfo.diagnostic_info.server_time.formatted_time}</span></p>
                      </div>
                    </div>
                  )}
              
                  {/* Informações do Banco de Dados */}
                  {diagnosticInfo.diagnostic_info && diagnosticInfo.diagnostic_info.database && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="font-medium mb-2 text-gray-900">Banco de Dados</h3>
                      <div className="space-y-2">
                        <p className="text-gray-800">Status: <span className="font-medium text-green-600">Conectado</span></p>
                        <p className="text-gray-800">Registros: <span className="font-medium">{diagnosticInfo.diagnostic_info.database.record_count}</span></p>
                        {diagnosticInfo.diagnostic_info.database.tables && (
                          <div>
                            <p className="text-gray-800 mb-1">Tabelas:</p>
                            <ul className="list-disc pl-5">
                              {diagnosticInfo.diagnostic_info.database.tables.map((table: any) => (
                                <li key={table.name} className="text-gray-700">
                                  {table.name}: {table.count} registros
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Informações do Sistema */}
              {diagnosticInfo.diagnostic_info && diagnosticInfo.diagnostic_info.system && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">Informações do Sistema</h2>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-gray-800">Sistema: <span className="font-medium">{diagnosticInfo.diagnostic_info.system.platform}</span></p>
                    <p className="text-gray-800">Memória Total: <span className="font-medium">{(diagnosticInfo.diagnostic_info.system.totalMemory / (1024 * 1024 * 1024)).toFixed(2)} GB</span></p>
                    <p className="text-gray-800">Memória Livre: <span className="font-medium">{(diagnosticInfo.diagnostic_info.system.freeMemory / (1024 * 1024 * 1024)).toFixed(2)} GB</span></p>
                    <p className="text-gray-800">Uso de CPU: <span className="font-medium">{(diagnosticInfo.diagnostic_info.system.cpuUsage * 100).toFixed(1)}%</span></p>
                    <p className="text-gray-800">Uptime: <span className="font-medium">{Math.floor(diagnosticInfo.diagnostic_info.system.uptime / 86400)} dias, {Math.floor((diagnosticInfo.diagnostic_info.system.uptime % 86400) / 3600)} horas</span></p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DiagnosticPage; 